/**
 * useFormValidation - Hook for form validation in scenario editor
 *
 * Calculates required fields and validates form state for enabling
 * navigation to advanced tabs (System, Financial, Analysis, Results).
 *
 * @module hooks/useFormValidation
 */

import { useMemo } from 'react';

/**
 * Validates scenario form and returns required/missing fields
 *
 * @param {Object} params
 * @param {Object} params.scenarioForm - The scenario form state object
 * @param {Object} params.loadProfile - The load profile state object
 * @param {Object|null} params.optimizationResults - Optimization results (if any)
 * @returns {Object} Validation state
 */
export function useFormValidation({ scenarioForm, loadProfile, optimizationResults = null }) {
    /**
     * Required fields for optimization submission
     * Each field has:
     *   - label: Human-readable name
     *   - value: Boolean indicating if field is valid
     *   - section: Tab section the field belongs to
     *   - hint: (optional) Help text for invalid fields
     */
    const requiredFields = useMemo(() => ({
        zipCode: {
            label: 'Zip Code',
            value: scenarioForm.zipCode && scenarioForm.zipCode.length === 5,
            section: 'site'
        },
        location: {
            label: 'Valid Location (geocoded)',
            value: scenarioForm.tariffLocation?.lat && scenarioForm.tariffLocation?.lon,
            section: 'site',
            hint: 'Enter a valid zip code or address to get coordinates'
        },
        loadProfile: {
            label: 'Load Profile (8760 hours)',
            value: loadProfile.loads?.length === 8760,
            section: 'load'
        },
        tariff: {
            label: 'Tariff Selection',
            value: scenarioForm.offGrid ||
                   scenarioForm.customTariffData ||
                   scenarioForm.useManualRate ||
                   (scenarioForm.blendedEnergyRate && parseFloat(scenarioForm.blendedEnergyRate) > 0),
            section: 'tariff'
        },
        // Voltage level required only when CA DER charges are enabled
        voltageLevel: {
            label: 'Voltage Level',
            value: scenarioForm.offGrid ||
                   !scenarioForm.caDerConfig || // CA DER not enabled - voltage level not needed
                   !!scenarioForm.customTariffData?.voltage_level ||
                   !!scenarioForm.selectedTariffData?.voltage_level ||
                   !!scenarioForm.selectedTariffData?.rate_data?.voltage_level,
            section: 'tariff',
            hint: 'Voltage level required for CA DER charge calculations. Configure on the CA DER Charges tab.'
        },
        // Generator fuel type required when generator is enabled
        generatorFuelType: {
            label: 'Generator Fuel Type',
            value: !scenarioForm.useGenerator || !!scenarioForm.generatorFuelType,
            section: 'site',
            hint: 'Select fuel type (Diesel, Natural Gas, or Propane) for generator'
        },
    }), [
        scenarioForm.zipCode,
        scenarioForm.tariffLocation,
        loadProfile.loads,
        scenarioForm.offGrid,
        scenarioForm.useManualRate,
        scenarioForm.customTariffData,
        scenarioForm.selectedTariffData,
        scenarioForm.useGenerator,
        scenarioForm.generatorFuelType,
        scenarioForm.blendedEnergyRate,
        scenarioForm.caDerConfig
    ]);

    /**
     * List of fields that are missing/invalid
     * Each item has: { key, label, section, hint? }
     */
    const missingFields = useMemo(() => {
        return Object.entries(requiredFields)
            .filter(([key, field]) => !field.value)
            .map(([key, field]) => ({ key, ...field }));
    }, [requiredFields]);

    /**
     * Allow navigation if all required fields are valid OR if results already exist
     * This allows users to view existing results even if new validation rules were added
     */
    const hasExistingResults = !!optimizationResults;
    const canProceedToNextTab = missingFields.length === 0 || hasExistingResults;

    /**
     * Check if all required fields are valid (strict mode)
     */
    const isFormValid = missingFields.length === 0;

    /**
     * Get missing fields for a specific section
     */
    const getMissingFieldsForSection = (section) => {
        return missingFields.filter(field => field.section === section);
    };

    return {
        requiredFields,
        missingFields,
        canProceedToNextTab,
        isFormValid,
        hasExistingResults,
        getMissingFieldsForSection,
    };
}

export default useFormValidation;
